import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import Icon from "../../2/Controls/Icon";
import TemplatePage from "./TemplatePage";
import TemplatePreview from "./TemplatePreview";
import TemplateSettings from "./TemplateSettings";
import ConfirmDialog from "./ConfirmDialog";
import DiscardDialog from "./DiscardDialog";
import BankDetailsSheet from "./BankDetailsSheet";
import QrSheet from "./QrSheet";
import SignatureSheet from "./SignatureSheet";
import withStoreTemplates from "./withStoreTemplates";
import { SETTING_KEYS } from "./printFields";
import { isMobileApp } from "../../../actions/Util";
import {
  getPreviewHeight,
  getStartingConfig,
  getTemplates,
  getTransactionType,
  keepDefault,
  readExtras,
  renderTemplate,
  sampleData,
  storeLocation,
  toPayload,
  writeExtras
} from "./templateUtils";

// Each save keeps what it replaces, so a change can be undone
const MAX_VERSIONS = 3;

// One template: everything that can be done to it happens here. A starter is a
// gallery design: a new template, or the new look of an existing one.
class PrintTemplateCustomize extends Component {
  constructor(props) {
    super(props);
    this.existing = getTemplates(props, this.typeKey).find(
      (t) => t.key === props.match.params.configKey
    );
    this.starter = (props.location.state || {}).starter;

    const saved = this.existing
      ? this.existing.PrintTemplate
      : this.starter && this.starter.template;
    this.original = {
      name: this.existing ? this.existing.PrintDisplayName : this.getNewName(),
      source: saved,
      extras: readExtras(saved)
    };
    this.state = {
      name: this.original.name,
      source: this.starter ? this.starter.template : saved,
      settings: this.getDesignChange(),
      extras: this.original.extras,
      resetKey: 0,
      showBank: false,
      showQr: false,
      showSignature: false,
      showDelete: false,
      discardTo: null,
      saving: false
    };
  }

  get typeKey() {
    return this.props.match.params.typeKey;
  }

  get size() {
    return this.starter ? this.starter.size : this.existing.PrintSize;
  }

  get isDefault() {
    return (
      !!this.existing &&
      getTemplates(this.props, this.typeKey)[0].key === this.existing.key
    );
  }

  // Height and orientation belong to the old paper. Print.js uses a saved height
  // on any size, so an A4 height would stretch a 3 inch receipt.
  getDesignChange() {
    if (!this.existing || !this.starter) return {};
    return this.starter.size === this.existing.PrintSize
      ? { PrintSize: this.starter.size }
      : { PrintSize: this.starter.size, PrintHeight: 0, PrintOrientation: "" };
  }

  getNewName() {
    const type = getTransactionType(this.props, this.typeKey);
    return `${type ? type.TransactionType : ""} ${this.starter ? this.starter.name : ""}`.trim();
  }

  isDirty() {
    const { name, source, settings, extras } = this.state;
    return (
      name !== this.original.name ||
      source !== this.original.source ||
      Object.keys(settings).length > 0 ||
      JSON.stringify(extras) !== JSON.stringify(this.original.extras)
    );
  }

  getTemplate() {
    return writeExtras(this.state.source, this.state.extras, this.size);
  }

  // Settings as they stand: the saved ones with this session's changes on top
  getConfig() {
    return {
      ...(this.existing || getStartingConfig(this.props, this.typeKey, this.size)),
      ...this.state.settings
    };
  }

  getVersions() {
    const { existing } = this;
    const versions = (existing && existing.PreviousVersions) || [];
    if (!existing || !this.isDirty()) return versions;

    const settings = {};
    SETTING_KEYS.filter((k) => existing[k] !== undefined).forEach((k) => {
      settings[k] = existing[k];
    });
    return [
      {
        SavedOn: Date.now(),
        PrintDisplayName: existing.PrintDisplayName,
        PrintTemplate: existing.PrintTemplate,
        Settings: settings
      },
      ...versions
    ].slice(0, MAX_VERSIONS);
  }

  // Kept versions make the record big. If the server refuses it, the edit itself
  // matters more than the oldest version.
  async saveTemplate(template, key) {
    let versions = this.getVersions();
    for (;;) {
      try {
        return await this.props.savePrintConfig(
          { ...template, PreviousVersions: versions },
          key
        );
      } catch (error) {
        if (versions.length === 0) throw error;
        versions = versions.slice(0, -1);
      }
    }
  }

  goToList = () => {
    this.props.history.replace(storeLocation(this.props.storeKey));
  };

  goToGallery(state) {
    this.props.history.push(
      storeLocation(this.props.storeKey, `/${this.typeKey}/gallery`, state)
    );
  }

  // Unsaved work only goes after the user agrees
  leave(go) {
    if (this.isDirty()) {
      this.setState({ discardTo: go });
    } else {
      go();
    }
  }

  onBack = () => {
    this.leave(this.existing ? this.goToList : this.props.history.goBack);
  };

  onChangeTemplate = () => {
    this.leave(
      this.existing
        ? () => this.goToGallery({ size: this.size, replaceKey: this.existing.key })
        : this.props.history.goBack
    );
  };

  onAddTemplate = () => {
    this.leave(() => this.goToGallery({ size: this.size }));
  };

  onSettingsChange = (change) => {
    this.setState({ settings: { ...this.state.settings, ...change } });
  };

  onRestore = (version) => {
    this.setState({
      name: version.PrintDisplayName,
      source: version.PrintTemplate,
      extras: readExtras(version.PrintTemplate),
      settings: version.Settings || {},
      resetKey: this.state.resetKey + 1
    });
    this.props.getToastr("Version restored. Tap Save Template to keep it.");
  };

  onSetDefault = async () => {
    if (this.isDirty()) {
      this.props.getToastr("Please save your changes first");
      return;
    }
    this.setState({ saving: "default" });
    try {
      await keepDefault(this.props, this.existing);
      await this.props.reloadTemplates();
      this.props.getToastr("Default template updated");
    } catch (error) {
      this.props.getToastr("Could not update the default template");
    }
    this.setState({ saving: false });
  };

  onDelete = async () => {
    this.setState({ saving: "delete" });
    try {
      const deleted = await this.props.deletePrintConfig(this.existing.key);
      if (!deleted) {
        throw new Error("Print config was not deleted");
      }
      await this.props.reloadTemplates();
      this.props.getToastr("Template deleted");
      this.goToList();
    } catch (error) {
      this.props.getToastr("Could not delete the template");
      this.setState({ saving: false, showDelete: false });
    }
  };

  onSave = async () => {
    const name = this.state.name.trim();
    if (name === "") {
      this.props.getToastr("Please enter a template name");
      return;
    }

    const defaultTemplate = getTemplates(this.props, this.typeKey)[0];
    const config = this.existing
      ? toPayload(this.existing)
      : getStartingConfig(this.props, this.typeKey, this.size);
    const key = this.existing ? this.existing.key : "";

    this.setState({ saving: "save" });
    try {
      const savedKey = await this.saveTemplate(
        {
          ...config,
          ...this.state.settings,
          PrintDisplayName: name,
          PrintTemplate: this.getTemplate()
        },
        key
      );
      if (!savedKey) {
        throw new Error("Print config was not saved");
      }
      if (defaultTemplate && defaultTemplate.key !== savedKey) {
        await keepDefault(this.props, defaultTemplate);
      }
      await this.props.reloadTemplates();
      if (this.existing) {
        this.props.getToastr("The template has been successfully updated.");
      } else if (defaultTemplate) {
        this.props.getToastr("Template added. Open it and use Set as default to print with it.");
      } else {
        this.props.getToastr("The template has been successfully added.");
      }
      this.goToList();
    } catch (error) {
      this.props.getToastr("Could not save the template. Please try again.");
      this.setState({ saving: false });
    }
  };

  renderDetails() {
    return (
      <React.Fragment>
        <div className="pt-name">
          <label htmlFor="pt-name">Template name</label>
          <input
            id="pt-name"
            className="form-control"
            value={this.state.name}
            onChange={(e) => this.setState({ name: e.target.value })}
          />
        </div>
        {this.isDefault && (
          <div className="pt-default-tag">
            <span className="pt-tag pt-tag--default">Default</span>
          </div>
        )}
      </React.Fragment>
    );
  }

  getExtraRows() {
    const { bank, qr, signature } = this.state.extras;
    const row = (title, value, sheet) => ({
      title,
      summary: value ? "Added" : "Not added",
      onClick: () => this.setState({ [sheet]: true })
    });
    return [
      row("Bank Details", bank, "showBank"),
      row("QR Code", qr, "showQr"),
      row("Signature", signature, "showSignature")
    ];
  }

  renderVersions() {
    const versions = (this.existing && this.existing.PreviousVersions) || [];
    if (versions.length === 0) return null;
    return (
      <div className="pt-versions">
        <div className="pt-section-title">Previous Versions</div>
        {versions.map((version) => (
          <div key={version.SavedOn} className="pt-version">
            <span>
              <strong>{version.PrintDisplayName}</strong>
              {new Date(version.SavedOn).toLocaleString("en-GB", {
                dateStyle: "medium",
                timeStyle: "short"
              })}
            </span>
            <button type="button" onClick={() => this.onRestore(version)}>
              Restore
            </button>
          </div>
        ))}
      </div>
    );
  }

  render() {
    if (!this.existing && !this.starter) {
      return <Redirect to={storeLocation(this.props.storeKey)} />;
    }

    const { extras, saving, discardTo } = this.state;
    const type = getTransactionType(this.props, this.typeKey);
    const typeName = type ? type.TransactionType : "";
    const stacked = this.existing ? " is-stacked" : "";
    const canSwitch = this.existing && !this.isDefault;

    return (
      <TemplatePage
        {...this.props}
        showBack
        onBack={this.onBack}
        title={this.state.name || typeName}
        subtitle={typeName}
      >
        <div className={"pt-workspace" + stacked}>
          <div className="pt-canvas">
            <TemplatePreview
              html={renderTemplate(
                this.getTemplate(),
                sampleData(this.getConfig(), this.props.store)
              )}
              size={this.size}
              maxHeight={getPreviewHeight()}
              trueSize={!isMobileApp()}
            />
          </div>
          <aside className="pt-panel">
            <TemplateSettings
              config={this.getConfig()}
              size={this.size}
              resetKey={this.state.resetKey}
              details={this.renderDetails()}
              extraRows={this.getExtraRows()}
              storageFolder={this.props.registration.registration.StorageFolder + "/PrintConfig"}
              uploadFileToServer={this.props.uploadFileToServer}
              getToastr={this.props.getToastr}
              onChange={this.onSettingsChange}
            />
            {this.renderVersions()}
          </aside>
        </div>

        <div className={"pt-bar" + stacked}>
          <button
            type="button"
            className="pt-btn pt-btn--secondary pt-btn--half"
            disabled={saving}
            onClick={this.onChangeTemplate}
          >
            Change template
          </button>
          {this.existing && (
            <button
              type="button"
              className="pt-btn pt-btn--secondary pt-btn--half"
              disabled={saving}
              onClick={this.onAddTemplate}
            >
              Add new template
            </button>
          )}
          {canSwitch && (
            <button
              type="button"
              className="pt-btn pt-btn--delete"
              aria-label="Delete"
              disabled={saving}
              onClick={() => this.setState({ showDelete: true })}
            >
              <Icon type="delete" />
              <span>Delete</span>
            </button>
          )}
          {canSwitch && (
            <button
              type="button"
              className="pt-btn pt-btn--secondary"
              disabled={saving}
              onClick={this.onSetDefault}
            >
              {saving === "default" ? <i className="fa fa-spinner fa-spin" /> : "Set as default"}
            </button>
          )}
          <button
            type="button"
            className="pt-btn pt-btn--primary"
            disabled={saving}
            onClick={this.onSave}
          >
            {saving === "save" ? <i className="fa fa-spinner fa-spin" /> : "Save Template"}
          </button>
        </div>

        {this.state.showBank && (
          <BankDetailsSheet
            bank={extras.bank}
            onClose={() => this.setState({ showBank: false })}
            onSave={(bank) =>
              this.setState({ extras: { ...extras, bank }, showBank: false })
            }
          />
        )}
        {this.state.showQr && (
          <QrSheet
            qr={extras.qr}
            onClose={() => this.setState({ showQr: false })}
            onSave={(qr) => this.setState({ extras: { ...extras, qr }, showQr: false })}
          />
        )}
        {this.state.showSignature && (
          <SignatureSheet
            signature={extras.signature}
            onClose={() => this.setState({ showSignature: false })}
            onSave={(signature) =>
              this.setState({ extras: { ...extras, signature }, showSignature: false })
            }
          />
        )}
        {this.state.showDelete && (
          <ConfirmDialog
            message={`Delete ${this.existing.PrintDisplayName}? This cannot be undone.`}
            cancelLabel="Cancel"
            confirmLabel="Delete"
            danger
            busy={saving === "delete"}
            onCancel={() => this.setState({ showDelete: false })}
            onConfirm={this.onDelete}
          />
        )}
        {discardTo && (
          <DiscardDialog
            onStay={() => this.setState({ discardTo: null })}
            onLeave={discardTo}
          />
        )}
      </TemplatePage>
    );
  }
}

export default withStoreTemplates(PrintTemplateCustomize);
